import { useState } from 'react'
import {
  Mail, MessageSquare, Linkedin, Phone, AlertCircle,
  X, Check, Send, Reply, Archive, Eye, MoreVertical
} from 'lucide-react'

export default function AIControlCenterPage() {
  const [selectedItem, setSelectedItem] = useState(null)
  const [responseText, setResponseText] = useState('')
  const [filter, setFilter] = useState('all') // all, pending, escalated

  // Mock data organized by platform
  const channels = {
    email: {
      name: 'Email Support',
      icon: Mail,
      color: 'blue',
      accounts: [
        { id: 'icloud', name: 'Apps Support', email: 'support@cybertection-guardbot.com', type: 'icloud' },
        { id: 'proton', name: 'Training Center', email: 'josephplaugher@cybertection-training-center.com', type: 'proton' }
      ],
      items: [
        {
          id: 'e1',
          from: 'customer@gmail.com',
          fromName: 'John Smith',
          subject: 'Billing Issue - Urgent',
          preview: 'I was charged twice for my subscription...',
          status: 'escalated',
          account: 'icloud',
          sentiment: 'negative',
          timestamp: '5 min ago',
          body: 'I was charged twice for my subscription and I need a refund immediately. This is unacceptable.',
          aiResponse: null,
          closed: false
        },
        {
          id: 'e2',
          from: 'user@work.com',
          fromName: 'Sarah Johnson',
          subject: 'Feature Request',
          preview: 'Love the new dashboard! Any chance...',
          status: 'ai_responded',
          account: 'icloud',
          sentiment: 'positive',
          timestamp: '22 min ago',
          body: 'Love the new dashboard! Any chance you could add dark mode?',
          aiResponse: 'Thank you for the feedback! Dark mode is on our roadmap for next quarter.',
          closed: false
        },
        {
          id: 'e3',
          from: 'student@training.com',
          fromName: 'Mike Chen',
          subject: 'Course Access Problem',
          preview: 'I purchased the Python course but cannot access it...',
          status: 'pending',
          account: 'proton',
          sentiment: 'neutral',
          timestamp: '1 hour ago',
          body: 'I purchased the Python course but cannot access the materials. Can you help?',
          aiResponse: 'Our system shows your payment is processing. This usually takes 2-4 hours.',
          closed: false
        }
      ]
    },
    discord: {
      name: 'Discord Support',
      icon: MessageSquare,
      color: 'purple',
      channels: ['#student-help', '#python-help', '#exercises'],
      items: [
        {
          id: 'd1',
          from: 'StudentA#1234',
          fromName: 'StudentA',
          channel: '#student-help',
          content: 'How do I fix this Python error: TypeError on line 42?',
          preview: 'How do I fix this Python error...',
          status: 'escalated',
          sentiment: 'neutral',
          timestamp: '3 min ago',
          confidence: 32,
          aiResponse: null,
          closed: false
        },
        {
          id: 'd2',
          from: 'StudentB#5678',
          fromName: 'StudentB',
          channel: '#exercises',
          content: 'Thanks for the explanation! That makes sense now.',
          preview: 'Thanks for the explanation...',
          status: 'ai_responded',
          sentiment: 'positive',
          timestamp: '15 min ago',
          confidence: 95,
          aiResponse: 'Happy to help! Feel free to ask if you have more questions.',
          closed: false
        },
        {
          id: 'd3',
          from: 'StudentC#9999',
          fromName: 'StudentC',
          channel: '#python-help',
          content: 'What is the best approach for the capstone project?',
          preview: 'What is the best approach...',
          status: 'pending',
          sentiment: 'neutral',
          timestamp: '45 min ago',
          confidence: 0,
          aiResponse: 'I recommend starting with outlining your project scope, then breaking it into modules.',
          closed: false
        }
      ]
    },
    linkedin: {
      name: 'LinkedIn Page',
      icon: Linkedin,
      color: 'cyan',
      items: [
        {
          id: 'l1',
          from: 'John CEO Inc',
          fromName: 'John - CEO',
          type: 'direct_message',
          content: 'We\'d like to explore a partnership opportunity with Cybertection.',
          preview: 'Partnership opportunity...',
          status: 'escalated',
          sentiment: 'neutral',
          timestamp: '18 min ago',
          aiResponse: null,
          closed: false
        },
        {
          id: 'l2',
          from: 'Sarah Marketing',
          fromName: 'Sarah Marketing Director',
          type: 'post_comment',
          content: 'Great article! Love the insights on AI in customer support.',
          preview: 'Great article! Love the insights...',
          status: 'ai_responded',
          sentiment: 'positive',
          timestamp: '1 hour ago',
          aiResponse: 'Thank you for reading! We\'re excited about the future of AI support.',
          closed: false
        },
        {
          id: 'l3',
          from: 'Tech Recruiter',
          fromName: 'Tech Recruiter',
          type: 'direct_message',
          content: 'Hi! Interested in hiring opportunities?',
          preview: 'Interested in hiring opportunities...',
          status: 'pending',
          sentiment: 'neutral',
          timestamp: '3 hours ago',
          aiResponse: 'Thanks for your interest! Please reach out to our HR team.',
          closed: false
        }
      ]
    },
    calls: {
      name: 'TextNow Calls & SMS',
      icon: Phone,
      color: 'green',
      items: [
        {
          id: 'c1',
          from: '+1-412-555-0123',
          fromName: 'Unknown Caller',
          type: 'call',
          content: 'Customer was frustrated about refund status',
          preview: 'Frustrated about refund...',
          status: 'escalated',
          sentiment: 'negative',
          timestamp: '2 min ago',
          duration: '4:32',
          transcript: 'Customer called asking about refund status. Was getting increasingly frustrated.',
          aiResponse: null,
          closed: false
        },
        {
          id: 'c2',
          from: '+1-412-555-0124',
          fromName: 'SMS Customer',
          type: 'sms',
          content: 'Thanks for your help! Issue resolved.',
          preview: 'Thanks for your help...',
          status: 'ai_responded',
          sentiment: 'positive',
          timestamp: '35 min ago',
          aiResponse: 'Happy to help! Have a great day!',
          closed: false
        },
        {
          id: 'c3',
          from: '+1-412-555-0125',
          fromName: 'Inquiry',
          type: 'call',
          content: 'Voicemail about product features',
          preview: 'Product feature inquiry...',
          status: 'pending',
          sentiment: 'neutral',
          timestamp: '2 hours ago',
          transcript: 'Customer left voicemail asking about premium features.',
          aiResponse: 'Thank you for your inquiry! Our premium plan includes...',
          closed: false
        }
      ]
    }
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'escalated': return 'bg-red-500/20 text-red-200 border-red-500/30'
      case 'ai_responded': return 'bg-green-500/20 text-green-200 border-green-500/30'
      case 'pending': return 'bg-yellow-500/20 text-yellow-200 border-yellow-500/30'
      default: return 'bg-slate-700/20 text-blue-200'
    }
  }

  const getSentimentBadge = (sentiment) => {
    switch(sentiment) {
      case 'positive': return '😊 Positive'
      case 'negative': return '😠 Negative'
      case 'neutral': return '😐 Neutral'
      default: return sentiment
    }
  }

  const handleRespond = async (itemId, channelType) => {
    console.log('Responding to', itemId, 'in', channelType, ':', responseText)
    // Would call API endpoint
    alert('Response sent! (Mock)')
    setResponseText('')
    setSelectedItem(null)
  }

  const handleClose = async (itemId, channelType) => {
    console.log('Closing', itemId)
    // Would call API endpoint
    alert('Item closed! (Mock)')
    setSelectedItem(null)
  }

  const handleEscalate = async (itemId, channelType) => {
    console.log('Escalating', itemId)
    // Would call API endpoint
    alert('Item escalated to high priority! (Mock)')
  }

  const renderChannelSection = (channelKey, channelData) => {
    const Icon = channelData.icon
    const filteredItems = channelData.items.filter(item => {
      if (filter === 'all') return true
      return item.status === filter
    })

    return (
      <div key={channelKey} className="card mb-6">
        {/* Channel Header */}
        <div className="bg-slate-800/50 border-b-2 border-blue-500/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon className="text-blue-400" size={24} />
              <div>
                <h2 className="text-xl font-bold text-white">{channelData.name}</h2>
                <p className="text-sm text-blue-200">{filteredItems.length} items</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-400">{filteredItems.length}</p>
              <p className="text-xs text-blue-200">
                {filteredItems.filter(i => i.status === 'escalated').length} escalated
              </p>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="card-body space-y-2">
          {filteredItems.length === 0 ? (
            <p className="text-center py-8 text-blue-300/70">No items</p>
          ) : (
            filteredItems.map(item => (
              <div
                key={item.id}
                onClick={() => setSelectedItem({ ...item, channelType: channelKey })}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedItem?.id === item.id ? 'border-blue-500/60 bg-slate-800/70' : 'border-blue-500/30 hover:border-blue-500/40'
                } ${item.closed ? 'opacity-50 line-through' : ''}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="font-semibold text-white">{item.fromName}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status === 'escalated' ? '🚨' : item.status === 'ai_responded' ? '✓' : '⏳'} {item.status}
                      </span>
                    </div>
                    <p className="text-sm text-blue-200">
                      {item.subject || item.channel || item.type === 'call' ? `${item.type === 'call' ? '📞' : '💬'} ${item.content?.substring(0, 60)}...` : item.preview}
                    </p>
                    <div className="flex items-center space-x-3 mt-2 text-xs text-blue-300/70">
                      <span>{getSentimentBadge(item.sentiment)}</span>
                      <span>•</span>
                      <span>{item.timestamp}</span>
                      {item.confidence !== undefined && (
                        <>
                          <span>•</span>
                          <span className={item.confidence < 50 ? 'text-orange-600 font-medium' : ''}>
                            {item.confidence}% confident
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {!item.closed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        console.log('Menu for item:', item.id)
                      }}
                      className="ml-2 text-blue-300 hover:text-blue-200 cursor-pointer transition-colors">
                      <MoreVertical size={18} />
                    </button>
                  )}
                </div>

                {item.closed && (
                  <div className="text-center text-sm text-green-600 font-medium">✓ CLOSED</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Channel Sections */}
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">AI Control Center</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' ? 'bg-cyber-600 text-white' : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('escalated')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'escalated' ? 'bg-cyber-600 text-white' : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              🚨 Escalated
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending' ? 'bg-cyber-600 text-white' : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              ⏳ Pending
            </button>
          </div>
        </div>

        {/* Email Section */}
        {renderChannelSection('email', channels.email)}

        {/* Discord Section */}
        {renderChannelSection('discord', channels.discord)}

        {/* LinkedIn Section */}
        {renderChannelSection('linkedin', channels.linkedin)}

        {/* Calls Section */}
        {renderChannelSection('calls', channels.calls)}
      </div>

      {/* Right: Detail Panel */}
      <div className="lg:col-span-1">
        {selectedItem ? (
          <div className="card sticky top-6">
            {/* Header */}
            <div className="card-header bg-slate-800/30 flex items-center justify-between">
              <h3 className="font-semibold text-white">Details</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-blue-300/70 hover:text-blue-200"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="card-body space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
              {/* From */}
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide">From</p>
                <p className="font-semibold text-white">{selectedItem.fromName}</p>
                <p className="text-sm text-blue-200">{selectedItem.from}</p>
              </div>

              {/* Subject/Content Preview */}
              {selectedItem.subject && (
                <div>
                  <p className="text-xs text-blue-200 uppercase tracking-wide">Subject</p>
                  <p className="font-medium text-white">{selectedItem.subject}</p>
                </div>
              )}

              {selectedItem.channel && (
                <div>
                  <p className="text-xs text-blue-200 uppercase tracking-wide">Channel</p>
                  <p className="font-medium text-white">{selectedItem.channel}</p>
                </div>
              )}

              {selectedItem.type && (
                <div>
                  <p className="text-xs text-blue-200 uppercase tracking-wide">Type</p>
                  <p className="font-medium text-white capitalize">{selectedItem.type}</p>
                </div>
              )}

              {/* Full Message */}
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide mb-2">Message</p>
                <div className="bg-slate-700/30 p-3 rounded-lg border border-blue-500/30">
                  <p className="text-sm text-blue-200">
                    {selectedItem.body || selectedItem.content || selectedItem.transcript}
                  </p>
                </div>
              </div>

              {/* AI Response */}
              {selectedItem.aiResponse && (
                <div>
                  <p className="text-xs text-blue-200 uppercase tracking-wide mb-2">🤖 AI Response</p>
                  <div className="bg-blue-500/20 p-3 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-100">{selectedItem.aiResponse}</p>
                  </div>
                </div>
              )}

              {/* Status Badge */}
              <div className="pt-4 border-t">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedItem.status)}`}>
                  Status: {selectedItem.status}
                </span>
              </div>

              {/* Action Buttons */}
              {!selectedItem.closed && (
                <div className="space-y-2 pt-4 border-t">
                  {selectedItem.status === 'escalated' && (
                    <button className="w-full btn-primary flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700">
                      <Eye size={18} />
                      <span>Review Escalation</span>
                    </button>
                  )}

                  {selectedItem.status === 'pending' && !selectedItem.aiResponse && (
                    <button
                      onClick={() => {
                        setResponseText(selectedItem.aiResponse || '')
                      }}
                      className="w-full btn-primary flex items-center justify-center space-x-2"
                    >
                      <Send size={18} />
                      <span>Craft Response</span>
                    </button>
                  )}

                  {(selectedItem.status === 'pending' || selectedItem.status === 'ai_responded') && (
                    <>
                      {!responseText ? (
                        <button
                          onClick={() => setResponseText(selectedItem.aiResponse || 'Your response here...')}
                          className="w-full btn-primary flex items-center justify-center space-x-2"
                        >
                          <Reply size={18} />
                          <span>Send Response</span>
                        </button>
                      ) : (
                        <>
                          <textarea
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            className="input-base text-sm"
                            rows="4"
                            placeholder="Your response..."
                          />
                          <button
                            onClick={() => handleRespond(selectedItem.id, selectedItem.channelType)}
                            className="w-full btn-primary flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700"
                          >
                            <Send size={18} />
                            <span>Send Now</span>
                          </button>
                          <button
                            onClick={() => setResponseText('')}
                            className="w-full btn-secondary"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => handleClose(selectedItem.id, selectedItem.channelType)}
                    className="w-full btn-secondary flex items-center justify-center space-x-2 bg-green-500/30 text-green-200 border-green-500/30 hover:bg-green-500/40"
                  >
                    <Check size={18} />
                    <span>Mark Closed</span>
                  </button>

                  {selectedItem.status !== 'escalated' && (
                    <button
                      onClick={() => handleEscalate(selectedItem.id, selectedItem.channelType)}
                      className="w-full btn-secondary flex items-center justify-center space-x-2 bg-red-500/30 text-red-200 border-red-500/30 hover:bg-red-500/40"
                    >
                      <AlertCircle size={18} />
                      <span>Escalate</span>
                    </button>
                  )}
                </div>
              )}

              {selectedItem.closed && (
                <div className="text-center py-4 bg-green-500/20 rounded-lg border-2 border-green-500/30">
                  <p className="font-bold text-green-200">✓ This item is closed</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card h-96 flex items-center justify-center text-center">
            <div className="text-blue-300/70">
              <p className="text-lg font-semibold mb-2">Select an item</p>
              <p className="text-sm">Click any message to review and respond</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
